<template>
  <div id="app">
    <NetworkStatus />
    <AppAnnouncementsHost
      :announcements="visibleAnnouncements"
      mode="top"
      @click="handleAnnouncementClick"
      @dismiss="handleAnnouncementDismiss"
      @acknowledge="handleAnnouncementAcknowledge"
    />
    <AppAnnouncementsHost
      :announcements="visibleAnnouncements"
      mode="modal"
      @click="handleAnnouncementClick"
      @dismiss="handleAnnouncementDismiss"
      @acknowledge="handleAnnouncementAcknowledge"
    />

    <!-- Ensure Loader component is correctly placed -->
    <Loader />
    <router-view/>
    <PwaInstallButton />
  </div>
</template>

<script>
import Loader from "@/components/layout/Loader";
import NetworkStatus from "@/components/layout/NetworkStatus.vue";
import PwaInstallButton from "@/components/layout/PwaInstallButton.vue";
import AppAnnouncementsHost from "@/components/layout/announcements/AppAnnouncementsHost.vue";
import { announcementsService } from "@/Services/announcements.service";
import {Storage} from "@/Services/Storage";
import {LytningService} from "@/Services/LytningService";
import Swal from "sweetalert2";
import router from "@/router";

const ANNOUNCEMENT_DISMISSALS_KEY = "lytAnnouncementsDismissals";
const ANNOUNCEMENT_ACKNOWLEDGED_KEY = "lytAnnouncementsAcknowledged";

export default {
  components: { Loader, NetworkStatus, PwaInstallButton, AppAnnouncementsHost },
  data() {
    return {
      announcements: [],
      announcementContext: null,
      dismissedAnnouncements: {},
      acknowledgedAnnouncements: {},
    };
  },
  computed: {
    visibleAnnouncements() {
      return this.announcements.filter((announcement) => {
        if (!announcement || !announcement.id) return false;
        if (this.dismissedAnnouncements?.[announcement.id]) return false;
        if (announcement.requiresAcknowledgement && this.acknowledgedAnnouncements?.[announcement.id]) return false;
        return true;
      });
    },
  },
  created() {
    this.loadDismissedAnnouncements();
    this.loadAcknowledgedAnnouncements();
  },
  async mounted() {
    await this.validatePCRegistration();
    await this.checkLytningSubscriptionAndLogout();
    this.fetchAnnouncements();
  },
  methods: {
    validatePCRegistration() {
      return this.$store.dispatch('refreshState');
    },
    async checkLytningSubscriptionAndLogout() {
      try {
        const mobileUser = await Storage.getMobileUser();
        const allStores = await Storage.getAllStores();

        if (mobileUser && allStores && allStores.length > 0 && allStores[0].mainCustomerID) {
          const hasLytningSubscription = await LytningService.checkLytningSubscription(allStores[0].mainCustomerID);

          // if (hasLytningSubscription) {
          //   const storeNames = allStores.map(s => s.StoreName || s.name || s.storeName || 'Unknown Store').join(', ');
          //   await Swal.fire({
          //     title: 'Lyt-ning Subscription Detected',
          //     icon: 'warning',
          //     html: `<p>Your account has a Lyt-ning subscription.</p><p>The following stores are on the Lyt-ning package:</p><p><strong>${storeNames}</strong></p><p>Please use the Lytning platform to access your stores.</p><p>You will now be logged out.</p>`,
          //     confirmButtonText: 'OK',
          //     confirmButtonColor: '#3085d6',
          //     allowOutsideClick: false,
          //     allowEscapeKey: false
          //   }).then(async () => {
          //     await this.$store.dispatch('clearStoreState');
          //     try {
          //       await router.push({name: 'login'});
          //     } catch (e) {}
          //   });
          //   return;
          // }
        }
      } catch (error) {
        console.error('Lyt-ning subscription check failed:', error);
      }
    },
    loadDismissedAnnouncements() {
      try {
        const raw = localStorage.getItem(ANNOUNCEMENT_DISMISSALS_KEY);
        this.dismissedAnnouncements = raw ? JSON.parse(raw) : {};
      } catch {
        this.dismissedAnnouncements = {};
      }
    },
    persistDismissedAnnouncements() {
      try {
        localStorage.setItem(ANNOUNCEMENT_DISMISSALS_KEY, JSON.stringify(this.dismissedAnnouncements));
      } catch (e) {
        console.warn("[announcements] could not persist dismissals", e);
      }
    },
    loadAcknowledgedAnnouncements() {
      try {
        const raw = localStorage.getItem(ANNOUNCEMENT_ACKNOWLEDGED_KEY);
        this.acknowledgedAnnouncements = raw ? JSON.parse(raw) : {};
      } catch {
        this.acknowledgedAnnouncements = {};
      }
    },
    persistAcknowledgedAnnouncements() {
      try {
        localStorage.setItem(ANNOUNCEMENT_ACKNOWLEDGED_KEY, JSON.stringify(this.acknowledgedAnnouncements));
      } catch (e) {
        console.warn("[announcements] could not persist acknowledgements", e);
      }
    },
    async fetchAnnouncements() {
      try {
        const context = await announcementsService.resolveContext();
        this.announcementContext = context;
        this.announcements = await announcementsService.fetchVisibleAnnouncements();
      } catch (e) {
        console.warn("[announcements] failed to fetch", e);
        this.announcements = [];
      }
    },
    buildAnnouncementInteractionPayload(announcement) {
      return {
        announcement_id: announcement?.id,
        ...(this.announcementContext || {}),
      };
    },
    async handleAnnouncementDismiss(announcement) {
      if (!announcement?.id) return;
      this.$set(this.dismissedAnnouncements, announcement.id, Date.now());
      this.persistDismissedAnnouncements();
      try {
        await announcementsService.dismissAnnouncement(
          announcement.id,
          this.buildAnnouncementInteractionPayload(announcement),
        );
      } catch (e) {
        console.warn("[announcements] dismiss failed", e);
      }
    },
    async handleAnnouncementAcknowledge(announcement) {
      if (!announcement?.id) return;
      this.$set(this.acknowledgedAnnouncements, announcement.id, Date.now());
      this.persistAcknowledgedAnnouncements();
      try {
        await announcementsService.acknowledgeAnnouncement(
          announcement.id,
          this.buildAnnouncementInteractionPayload(announcement),
        );
      } catch (e) {
        console.warn("[announcements] acknowledge failed", e);
      }
    },
    async handleAnnouncementClick(announcement) {
      if (!announcement?.id) return;
      try {
        await announcementsService.trackAnnouncementClick(
          announcement.id,
          this.buildAnnouncementInteractionPayload(announcement),
        );
      } catch (e) {
        console.warn("[announcements] click tracking failed", e);
      }
      if (announcement.link) {
        const linkType = announcement.linkType || "external";
        if (linkType === "external") {
          window.open(announcement.link, "_blank", "noopener,noreferrer");
        } else {
          this.$router.push(announcement.link).catch(() => {});
        }
      }
    },
  }
}
</script>

<style lang="scss">
@import "assets/css/styles";

.accordion-button:active {
  transform: scale(1) !important;
}
</style>
